import { useCallback } from 'react';
import { useToast } from '@app/core';
import {
  Participation,
  ParticipationReportReason,
  useBulkCreateEvaluation,
  useBulkReportParticipation,
  useIsAdmin,
  SelectionPayload,
  IndexedParticipation,
} from '@app/api';

interface ReportReasonValues {
  reportReason: ParticipationReportReason;
  message?: string;
}

export function useSubmitReportReason(
  sessionId: string,
  selectionPayload?: SelectionPayload,
  participations?: Participation[],
) {
  const toast = useToast();
  const isAdmin = useIsAdmin();
  const { mutateAsync: reportParticipants } =
    useBulkReportParticipation(sessionId);
  const { mutateAsync: createEvaluation } = useBulkCreateEvaluation(sessionId);

  return useCallback(
    async (values: ReportReasonValues) => {
      if (!values.reportReason) {
        throw new Error('Report reason is required');
      }

      const payload: SelectionPayload = selectionPayload ?? {
        sessionId,
        selectionMode: 'partial',
        selectedIds: participations?.map((p) => p.id) || [],
        selectedParticipations: participations as IndexedParticipation[],
      };

      if (!isAdmin) {
        const score =
          values.reportReason === ParticipationReportReason.NOT_SERIOUS_ANSWERS
            ? -50
            : -100;

        await createEvaluation({
          ...payload,
          score,
          message: `${values.reportReason}: ${values.message || ''}`,
        });
      }

      await reportParticipants({
        ...payload,
        reportReason: values.reportReason,
      });

      toast.saved();
    },
    [
      selectionPayload,
      toast,
      isAdmin,
      reportParticipants,
      createEvaluation,
      sessionId,
      participations,
    ],
  );
}
